import { Http, HttpPromise } from "@shared/svelte/Modules/ApiService";
import { userNotifications } from "@retailer/stores/UserNotificationsStore"

// types
import type { HTTPParams } from "@shared/types/HTTP";
import type { UserNotificationResource, UserNotification } from '@shared/types/UserNotification';
import { get } from "svelte/store";
import { EventTracking } from "@shared/services/event_tracking/events";
import { NotificationIdsEntity } from "@shared/services/event_tracking/entities";

const { trackEvent } = EventTracking;

// constants
const baseUrl = "/api/user_notifications";

const makeReq = async (requestData: HTTPParams, successCB) => {
  userNotifications.update((state) => {
    return { ...state, loading: true, failed: false }
  });

  // callbacks
  const errCB = () => userNotifications.update(state => ({
    ...state,
    loading: false,
    failed: true,
  }));

  await Http(requestData, successCB, errCB)
}

export const getNotifications = async () => {
  const requestData: HTTPParams = {
    method: "GET",
    url: baseUrl,
  };

  // callbacks
  const successCB = (data) => {
    userNotifications.set(
      {
        unseen: data.unseen,
        seen: data.seen,
        recent: data.recent,
        loading: false,
        failed: false
      }
    )
  };

  await makeReq(requestData, (data) => successCB(data));
};

export const enableInvoiceNotifications = async () => {
  const requestData: HTTPParams = {
    method: "POST",
    url: "/api/retailer/notifications",
    data: {
      notification: {
        retailer_id: window["$rootScope"].currentRetailerId,
        name: "invoice_term_available",
        email_preference: true,
        phone_preference: true,
      },
    },
  };

  await HttpPromise(requestData);
};

export const markAsSeen = async (notification: UserNotification) => {
  const requestData: HTTPParams = {
    method: "PUT",
    url: `${baseUrl}/${notification.id}/see`,
  };

  // callbacks
  const successCB = (_data) => {
    userNotifications.update(state => {
      return {
        ...state,
        unseen: state.unseen.filter(u => u.id !== notification.id),
        seen: [...state.seen, notification],
        recent: state.recent.map(s => {
          if (s.id === notification.id) {
            return { ...s, seen_at: new Date().toISOString() };
          } else {
            return s;
          }
        }),
      };
    });
  };

  await makeReq(requestData, (data) => successCB(data));
};

export const markAllAsSeen = async () => {
  const notificationIds = get(userNotifications).recent.filter((n) => n.seen_at === null).map((n) => n.id);
  const requestData: HTTPParams = {
    method: "POST",
    url: '/api/user_notifications_bulk_see',
    data: { ids: notificationIds }
  };

  // callbacks
  const successCB = (_data) => {
    userNotifications.update(state => {
      return {
        ...state,
        unseen: state.unseen.filter(u => !notificationIds.includes(u.id)),
        seen: [...state.seen, ...state.recent.filter(u => notificationIds.includes(u.id))],
        recent: state.recent.map(u => {
          if (notificationIds.includes(u.id)) {
            return { ...u, seen_at: new Date().toISOString() };
          } else {
            return u;
          }
        }),
      };
    });
    trackEvent('notification_mark_all_read', new NotificationIdsEntity({notification_ids: notificationIds }));
  };

  await makeReq(requestData, (data) => successCB(data));
}

export const create = async (notificationData: UserNotificationResource) => {
  const requestData = {
    method: "POST",
    url: baseUrl,
    data: notificationData,
  };

  // callbacks
  const successCB = (data) => {
    userNotifications.update(state => ({
      ...state,
      seen: [
        ...state.seen,
        data
      ]
    }))
  };

  await makeReq(requestData, (data) => successCB(data));
};
